import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Image,
  Input,
  Spacer,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { franchiseMapIndex, titleMapIndex } from "../../../core/constants";
import {
  getPartnerReadingOrder,
  getPartnerReadingOrderComics,
  updatePartnerReadingOrderComics,
} from "../../../services/partnerApi";
import Spinner from "../../../shared/components/Spinner";
import { Card } from "../../auth/components/Login/Card";
import ComicAutoComplete from "../components/ComicAutoComplete";

function PartnerReadingOrderComicsForm() {
  const navigate = useNavigate();
  const toast = useToast();
  const [comics, setComics] = useState([]);
  const { id } = useParams();
  const readingOrderId = id;
  const { data, isSuccess } = useQuery(
    ["readingOrder", readingOrderId],
    () => getPartnerReadingOrder(readingOrderId),
    {
      refetchOnWindowFocus: false,
    }
  );

  const orderQuery = useQuery(
    ["readingOrderComics", readingOrderId],
    () => getPartnerReadingOrderComics(readingOrderId),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setComics(data.data);
      },
    }
  );

  const { mutateAsync, isLoading: mutationIsLoading } = useMutation(
    ({ readingOrderId, comics }) => {
      return updatePartnerReadingOrderComics(readingOrderId, comics);
    }
  );

  // Handle form submit
  function handleSubmit(event) {
    event.preventDefault();
    const values = comics.map((c, index) => ({
      order: index + 1,
      comicId: c.id,
      readingOrderId,
      story: c.story ? c.story : null,
    }));

    mutateAsync({ readingOrderId, comics: values }).then(() => {
      toast({
        title: "Reading order updated",
        description: "The comics order has been updated.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      navigate(`/partnership/reading-orders/all`);
    });
  }

  function handleAddBefore(index) {
    setComics((comics) => [
      ...comics.slice(0, index),
      {},
      ...comics.slice(index),
    ]);
  }

  // Handle comic removal
  function handleAddAfter(index) {
    setComics((comics) => [
      ...comics.slice(0, index + 1),
      {},
      ...comics.slice(index + 1),
    ]);
  }

  // Handle comic removal
  function handleRemove(index) {
    setComics((comics) => comics.filter((comic, i) => i !== index));
  }

  function handleAddOrReplace(index, comic) {
    if (index === comics.length) {
      setComics([...comics, comic]);
      return false;
    }

    setComics((comics) => comics.map((c, idx) => (idx === index ? comic : c)));
    return true;
  }

  function setStory(index, story) {
    setComics((comics) =>
      comics.map((c, idx) => (idx === index ? { ...c, story } : c))
    );
    return true;
  }

  return (
    <Container maxW="container.lg">
      <Box
        bg={useColorModeValue("gray.50", "inherit")}
        minH="100vh"
        w="100%"
        py="12"
        px={{ base: "4", lg: "8" }}
      >
        <Box mx="auto">
          {/* Breadcrumbs */}
          <Breadcrumb mb="3">
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/partnership/reading-orders/all">
                Reading Orders
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>Edit order</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          {/* Header */}
          {isSuccess && (
            <Box position="relative">
              {/* Overlay content */}
              <Box
                h="350px"
                w="100%"
                bgGradient="linear(to-t, black, transparent)"
                position="absolute"
                top="0"
                left="0"
              >
                <Box w="100%" position="absolute" bottom="0">
                  <Flex p="6">
                    <Box>
                      <Heading as="h3">{data?.data?.title}</Heading>
                      <Text fontSize="sm">
                        {franchiseMapIndex[data?.data?.franchise]} &bull;{" "}
                        {data?.data?.published ? "Publised" : "Draft"}
                        {titleMapIndex[data?.data?.type]}
                      </Text>
                    </Box>
                    <Spacer />
                  </Flex>
                </Box>
              </Box>

              {/* Backgroung image */}
              <Image
                src={data?.data?.thumbnail}
                alt="Banner"
                objectFit="cover"
                width="100%"
                h="350px"
              />
            </Box>
          )}

          {/* Form */}
          <Card>
            {orderQuery.isLoading && <Spinner />}
            {orderQuery.isSuccess && !orderQuery.isLoading && (
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Comics in order</FormLabel>
                    {comics.map((comic, index) => (
                      <Flex
                        alignItems="center"
                        key={`${index}-${comic.id}`}
                        pb="3"
                      >
                        <Box pr="2">
                          <Badge
                            bg="gray.600"
                            borderRadius="sm"
                            fontSize="0.8em"
                          >
                            {index + 1}
                          </Badge>
                        </Box>
                        <Box flex="1">
                          <ComicAutoComplete
                            franchise={data?.data?.franchise}
                            comic={comic}
                            index={index}
                            handleAddOrReplace={handleAddOrReplace}
                          />
                        </Box>
                        <Box px="2" w="120px">
                          <FormControl>
                            <Input
                              htmlSize={5}
                              placeholder="Story"
                              type="text"
                              name="story"
                              maxLength={1}
                              value={comic.story}
                              onChange={(e) => setStory(index, e.target.value)}
                            />
                          </FormControl>
                        </Box>
                        <Box pl="2">
                          <Flex>
                            <VStack>
                              <IconButton
                                size="xs"
                                aria-label="Add a row"
                                icon={
                                  <>
                                    <AddIcon />
                                    <ChevronUpIcon />
                                  </>
                                }
                                onClick={() => {
                                  handleAddBefore(index);
                                }}
                                bg="gray.800"
                                p="2"
                                mx="2"
                                color="white"
                                _hover={{ bgColor: "gray.600" }}
                              />
                              <IconButton
                                size="xs"
                                aria-label="Add a row"
                                icon={
                                  <>
                                    <AddIcon />
                                    <ChevronDownIcon />
                                  </>
                                }
                                onClick={() => {
                                  handleAddAfter(index);
                                }}
                                bg="gray.800"
                                p="2"
                                mx="2"
                                color="white"
                                _hover={{ bgColor: "gray.600" }}
                              />
                            </VStack>
                            <IconButton
                              size={"sm"}
                              aria-label="Clear search"
                              icon={<DeleteIcon />}
                              onClick={() => {
                                handleRemove(index);
                              }}
                              bg="gray.800"
                              m="2"
                              color="white"
                              _hover={{ bgColor: "gray.600" }}
                            />
                          </Flex>
                        </Box>
                      </Flex>
                    ))}
                    <Box
                      display="flex"
                      alignItems="center"
                      key={`${comics.length}-${0}`}
                      pb="3"
                    >
                      <Box pr="2">
                        <Badge bg="gray.600" borderRadius="sm" fontSize="0.8em">
                          {comics.length + 1}
                        </Badge>
                      </Box>
                      <ComicAutoComplete
                        index={comics.length}
                        franchise={data?.data?.franchise}
                        handleAddOrReplace={handleAddOrReplace}
                      />
                    </Box>
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="blue"
                    disabled={mutationIsLoading}
                  >
                    Update reading order
                  </Button>
                </VStack>
              </form>
            )}
          </Card>
        </Box>
      </Box>
    </Container>
  );
}

export default PartnerReadingOrderComicsForm;
